import { UiAction, SettingsPayload, Params } from '../types';
import React, { createContext, ReactNode } from 'react';
import { RouteManager } from '@ecosystem/newron-client-routes';
import { NavigateFunction } from 'react-router/dist/development/index';
import { PageSettingsManager } from '../page/settings';
import { useCorePageState } from '../page/provider';

type UiActionsContext = {
  perform: (action: UiAction, valuesOverride?: Params) => void;
};

export const uiActionsContext = createContext<UiActionsContext>({
  perform: () => {},
});

type Props = {
  children: ReactNode;
  navigate: NavigateFunction;
};
function UiActionsProvider({ children, navigate }: Props) {
  const { openModal } = useCorePageState();

  const navigateToPath = (path: string, settingsPayload?: SettingsPayload) => {
    if (settingsPayload) {
      PageSettingsManager.getInstance().preCacheSettings(settingsPayload);
    }
    navigate(path);
  };

  const navigateToRoute = async (slug: string, args?: Record<string, string[]>, settingsPayload?: SettingsPayload) => {
    const route = RouteManager.getInstance().getRoute(slug);
    if (!route) {
      console.error(`no route found for ${slug} - cannot navigate`);
      return;
    }
    const state = await route.state();
    if (state === 'hidden' || state === 'restricted') {
      console.error(`${slug} is not available for the current user - cannot navigate`);
      return;
    }
    const url = route.getUrl(args as any);
    navigateToPath(url, settingsPayload);
  };

  const perform = (action: UiAction, valuesOverride?: Params) => {
    overridePayloadValues(action, valuesOverride);

    const { type, payload } = action;
    switch (type) {
      case 'NAVIGATE_TO_PATH':
        navigateToPath(payload.path, payload.settings);
        break;
      case 'NAVIGATE_TO_ROUTE':
        navigateToRoute(payload.slug, payload.args, payload.settings);
        break;
      case 'OPEN_MODAL':
        openModal(payload.name, payload.endpoint, payload.params);
        break;
      default:
        // fails type checking if not all actions are accounted for above

        const _: never = type;
    }
  };

  const overridePayloadValues = (action: UiAction, valuesToOverride?: Params) => {
    if (!valuesToOverride) {
      return;
    }

    const { type, payload } = action;
    switch (type) {
      case 'NAVIGATE_TO_PATH':
      case 'NAVIGATE_TO_ROUTE':
        if (payload.settings) {
          Object.keys(valuesToOverride).forEach(key => {
            if (payload.settings.page?.[key]) {
              payload.settings.page[key] = valuesToOverride[key];
            }
            Object.keys(payload.settings.components || {}).forEach(component => {
              if (component[key]) {
                component[key] = valuesToOverride[key];
              }
            });
          });
        }
        break;
      case 'OPEN_MODAL':
        payload.params = {
          ...{},
          ...(payload.params || {}),
          ...(valuesToOverride || {}),
        };
        break;
      default:
        // fails type checking if not all actions are accounted for above

        const _: never = type;
    }
  };

  return <uiActionsContext.Provider value={{ perform }}>{children}</uiActionsContext.Provider>;
}

export { UiActionsProvider };
